// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { ScreenBrightness } from "@capacitor-community/screen-brightness";
import { useEffect } from "react";

import { isCapacitorPlatform } from "./capacitor";

/**
 * Hook for managing screen brightness with automatic restoration
 * @param targetBrightness - Brightness level from 0 to 1 (0 = darkest, 1 = brightest)
 * @param shouldApply - Whether to apply the brightness change
 * @param onError - Optional error handler for brightness operations
 */
export function useScreenBrightness(
  targetBrightness: number,
  shouldApply: boolean,
  onError?: (error: unknown, operation: "set" | "restore") => void
) {
  useEffect(() => {
    if (!isCapacitorPlatform()) {
      return undefined;
    }

    let applied = false;
    let originalBrightness: number | undefined;

    const applyBrightness = async () => {
      if (shouldApply) {
        try {
          // Get current brightness before changing it
          const result = await ScreenBrightness.getBrightness();
          const { brightness } = result ?? {};
          originalBrightness = typeof brightness === "number" ? brightness : undefined;

          // Set new brightness, clamped between 0 and 1
          const clampedBrightness = Math.max(0, Math.min(1, targetBrightness));
          await ScreenBrightness.setBrightness({ brightness: clampedBrightness });
          applied = true;
        } catch (error) {
          onError?.(error, "set");
        }
      }
    };

    void applyBrightness();

    return () => {
      if (applied && originalBrightness !== undefined) {
        const restorePromise = ScreenBrightness.setBrightness({
          brightness: originalBrightness,
        });
        if (restorePromise?.catch && typeof restorePromise.catch === "function") {
          void restorePromise.catch((error) => {
            onError?.(error, "restore");
          });
        }
      }
    };
  }, [targetBrightness, shouldApply]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: onError is intentionally omitted from deps to prevent infinite re-renders
  // since these functions are recreated on every parent component render
}
